import axios from "axios";
import type {
  AuthProviderToken,
  AuthStateSubscriber,
  AuthProviderName,
  MfaSession,
} from "./types";
import { LocalStorageTokenStore, type TokenStorage } from "./storage";

interface RefreshQueueItem {
  resolve: (token: string | null) => void;
  reject: (error: Error) => void;
}

export type RefreshHandler = (
  token: AuthProviderToken
) => Promise<AuthProviderToken | null>;

export type LogoutHandler = (token: AuthProviderToken) => Promise<void>;

export interface AuthServiceConfig {
  apiUrl: string;
  storage?: TokenStorage;
  /**
   * Per-provider custom refresh handlers. If a handler is provided for the
   * token's provider, it is used instead of the default backend refresh call.
   * Used by admin to delegate Google refresh to AWS Amplify.
   */
  refreshHandlers?: Partial<Record<AuthProviderName, RefreshHandler>>;
  /**
   * Per-provider custom logout handlers, invoked before storage is cleared.
   * Used by admin to delegate Google sign-out to AWS Amplify.
   */
  logoutHandlers?: Partial<Record<AuthProviderName, LogoutHandler>>;
}

export interface LogoutResult {
  /** The token state captured before logout cleanup. Null if not authenticated. */
  token: AuthProviderToken | null;
}

/**
 * Centralized authentication service - Single Source of Truth
 *
 * Responsibilities:
 * - Token storage and retrieval (via pluggable TokenStorage)
 * - Authorization header management
 * - Token refresh with queue management (single-flight per instance)
 * - Logout cleanup (auth-specific + optional aggressive storage wipe)
 * - MFA session management
 * - Auth state subscriptions
 */
export class AuthService {
  private readonly apiUrl: string;
  private readonly storage: TokenStorage;
  private readonly refreshHandlers: Partial<
    Record<AuthProviderName, RefreshHandler>
  >;
  private readonly logoutHandlers: Partial<
    Record<AuthProviderName, LogoutHandler>
  >;

  private isRefreshing = false;
  private refreshQueue: RefreshQueueItem[] = [];
  private readonly subscribers: Set<AuthStateSubscriber> = new Set();

  constructor(config: AuthServiceConfig) {
    this.apiUrl = config.apiUrl;
    this.storage = config.storage ?? new LocalStorageTokenStore();
    this.refreshHandlers = config.refreshHandlers ?? {};
    this.logoutHandlers = config.logoutHandlers ?? {};
  }

  // ─────────────────────────────────────────────────────────────
  // Token Operations
  // ─────────────────────────────────────────────────────────────

  getToken(): AuthProviderToken | null {
    return this.storage.getToken();
  }

  setToken(token: AuthProviderToken): void {
    this.storage.setToken(token);
    this.notifySubscribers();
  }

  clearToken(): void {
    this.storage.clearToken();
    this.notifySubscribers();
  }

  // ─────────────────────────────────────────────────────────────
  // Auth State
  // ─────────────────────────────────────────────────────────────

  isAuthenticated(): boolean {
    const token = this.getToken();
    return Boolean(token?.access_token);
  }

  getAccessToken(): string | null {
    return this.getToken()?.access_token ?? null;
  }

  getProvider(): AuthProviderName | null {
    return this.getToken()?.provider ?? null;
  }

  getAuthorizationHeader(): string | null {
    const accessToken = this.getAccessToken();
    return accessToken ? `Bearer ${accessToken}` : null;
  }

  // ─────────────────────────────────────────────────────────────
  // Token Refresh (with queue management)
  // ─────────────────────────────────────────────────────────────

  async refreshToken(): Promise<string | null> {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.refreshQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      const token = this.getToken();
      if (!token) {
        throw new Error("No token to refresh");
      }

      let newAccessToken: string | undefined;

      const customHandler = this.refreshHandlers[token.provider];
      if (customHandler) {
        const refreshed = await customHandler(token);
        if (refreshed?.access_token) {
          newAccessToken = refreshed.access_token;
          this.setToken(refreshed);
        }
      } else if (token.refresh_token) {
        const response = await axios.post<{
          access_token: string;
          token_type: string;
          refresh_token?: string;
        }>(`${this.apiUrl}/api/tokens/refresh`, {
          refresh_token: token.refresh_token,
        });

        if (response.status === 200 && response.data.access_token) {
          newAccessToken = response.data.access_token;
          // Use the new refresh_token if the server returns one (SSO tokens rotate),
          // otherwise keep the existing one (Cognito tokens are reusable).
          this.setToken({
            ...token,
            ...response.data,
            refresh_token: response.data.refresh_token ?? token.refresh_token,
          });
        }
      }

      this.processQueue(null, newAccessToken ?? null);
      return newAccessToken ?? null;
    } catch (error) {
      this.processQueue(error as Error, null);
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  private processQueue(error: Error | null, token: string | null): void {
    this.refreshQueue.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve(token);
      }
    });
    this.refreshQueue = [];
  }

  // ─────────────────────────────────────────────────────────────
  // Logout
  // ─────────────────────────────────────────────────────────────

  /**
   * Clears auth state. Returns the pre-logout token so callers (e.g. a Refine
   * authProvider) can implement provider-specific post-logout flows like
   * Scalekit SLO redirects.
   *
   * Storage cleanup delegates to `storage.clearAll()` if implemented; the
   * default LocalStorageTokenStore wipes ALL localStorage + sessionStorage to
   * match admin's existing security-driven logout behavior, except any keys
   * an app opted to keep via `preserveKeyPrefixes` (e.g. non-sensitive
   * "dismissed" flags).
   */
  async logout(options?: {
    waitBeforeProviderLogout?: Promise<unknown>;
  }): Promise<LogoutResult> {
    const token = this.getToken();

    // Clear our token + notify subscribers FIRST so reactive UI hooks observe
    // the logged-out state synchronously, BEFORE any slow per-provider
    // OAuth signout round-trip. Prevents a window where subscribers report
    // authenticated=true while Amplify (or any other handler) is still
    // awaiting an IdP call.
    this.storage.clearToken();
    this.resetRefreshState();
    this.notifySubscribers();

    // THEN await per-provider logout handler (e.g. Amplify signOutFromOAuth
    // for google). It must run before the full storage wipe below: Amplify
    // reads its own refresh token from localStorage to revoke it. If it
    // throws, log and continue — our token is already cleared.
    if (token) {
      const logoutHandler = this.logoutHandlers[token.provider];
      if (logoutHandler) {
        try {
          // Handler may redirect (Amplify → Cognito /logout), which would abort pending requests
          await options?.waitBeforeProviderLogout?.catch(() => undefined);
          await logoutHandler(token);
        } catch (e) {
          console.warn("Error in provider logout handler:", e);
        }
      }
    }

    if (this.storage.clearAll) {
      this.storage.clearAll();
    } else {
      this.storage.clearMfa();
    }

    return { token };
  }

  // ─────────────────────────────────────────────────────────────
  // MFA Session Management
  // ─────────────────────────────────────────────────────────────

  getMfaSession(): MfaSession | null {
    return this.storage.getMfa();
  }

  setMfaSession(session: MfaSession): void {
    this.storage.setMfa(session);
  }

  clearMfaSession(): void {
    this.storage.clearMfa();
  }

  // ─────────────────────────────────────────────────────────────
  // Subscription Pattern
  // ─────────────────────────────────────────────────────────────

  subscribe(callback: AuthStateSubscriber): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  private notifySubscribers(): void {
    const isAuth = this.isAuthenticated();
    this.subscribers.forEach((callback) => callback(isAuth));
  }

  // ─────────────────────────────────────────────────────────────
  // State Reset
  // ─────────────────────────────────────────────────────────────

  resetRefreshState(): void {
    this.isRefreshing = false;
    this.refreshQueue = [];
  }

  /** Full reset for tests only */
  resetAll(): void {
    this.resetRefreshState();
    this.subscribers.clear();
  }
}

export const createAuthService = (config: AuthServiceConfig): AuthService =>
  new AuthService(config);
